import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable, Citation } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames, countLabel, formatDateString, PLATFORM_DATE_FORMAT } from '@shapeable/utils';
import { Topic } from '@shapeable/gesda-types';
import { CellLabel, ContentEntityIntro, ContentNode, EntityLink, EntityProvider, LightMode, PersonValue, useIndicatorGroups, useLang, useMonthNames } from '@shapeable/ui';
import { TopicAnticipationPotentialChart } from '@shapeable/gesda-ui';
import { SubTopicViewLayout } from './sub-topic-view-layout';
import { TopicAnticipatoryImpactLayout } from './topic-anticipatory-impact-layout';
import { TopicPspIndicatorGroups } from './topic-psp-indicator-groups';
import { useCurrentEditionChairs } from '../../hooks/use-current-edition-chairs';
import { useCurrentEditionMembers } from '../../hooks/use-current-edition-members';
import { doiUrlFor, normalizeDoiId } from '../../utils/citation';
import { buildTopicBannerPhotoCredit } from '../../utils/topic-banner-photo-credit';
import GESDA_LOGO from '../../images/gesda-website_logo.png';
const cls = classNames('topic-print-layout');

const CREATIVE_COMMONS_BY_SA_URL = 'https://creativecommons.org/licenses/by-sa/4.0/';

// -------- Types -------->

type CitationEmbeds = {
  id?: string;
  slug?: string;
  name?: string;
  outlineNumber?: string;
  embeds?: {
    citations?: Citation[];
  };
  horizons?: CitationEmbeds[];
};

type CitationGroup = {
  id: string;
  title: string;
  citations: Citation[];
};

type ColorProps = {
  color?: string;
};

// -------- Props -------->

export type TopicPrintLayoutProps = Classable & HasChildren & { 
  entity?: Topic;
};

export const TopicPrintLayoutDefaultProps: Omit<TopicPrintLayoutProps, 'entity'> = {
};

// -------- Child Component Props -------->

type SectionLabelProps = {
  color?: string;
}

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    background: ${theme.COLOR('light')};
    color: ${theme.COLOR('text')};
    min-height: 100vh;
    padding: ${theme.UNIT(6)} 0;
  `,
});

const DocumentStyles = breakpoints({
  base: css`
    box-sizing: border-box;
    width: 210mm;
    max-width: 100%;
    margin: 0 auto;
    background: ${theme.COLOR('bright')};
    box-shadow: 0 0 ${theme.UNIT(4)} rgba(0, 0, 0, 0.12);

    @media print {
      width: 100%;
      max-width: none;
      margin: 0;
      box-shadow: none;
    }

    .shp--content {
      .shp--citation-reference__text {
        width: 12px;
        height: 12px;
        font-size: ${theme.FONT_SIZE(8)};
        top: -3px;
        left: 0;
      }
    }
  `,
});

const PageStyles = breakpoints({
  base: css`
    box-sizing: border-box;
    min-height: 297mm;
    padding: 18mm 16mm;
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(4)};
    break-after: page;
    page-break-after: always;

    &:last-child {
      break-after: auto;
      page-break-after: auto;
    }

    @media print {
      min-height: 0;
      padding: 0;
    }
  `,
});

const CoverStyles = breakpoints({
  base: css`
    justify-content: space-between;
    padding-top: 44px;

    @media print {
      min-height: 250mm;
    }
  `,
});

const CoverMediaStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
  `,
});

const BrandStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('primary')};
    font-size: 0.9em;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  `,
});

const CoverMainStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(5)};
  `,
});

const PretitleStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('dark')};
    font-family: ${theme.FONT('sans')};
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  `,
});

const TitleStyles = breakpoints({
  base: css`
    margin: 0;
    color: ${theme.COLOR('strong')};
    font-family: ${theme.FONT('sans')};
    font-size: 2em;
    font-weight: 400;
    line-height: 1.06;
  `,
});

const TitleRowStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(1)};
  `,
});

const OutlineBadgeStyles = breakpoints({
  base: css`
    align-items: center;
    background: ${({ color }: ColorProps) => color || theme.COLOR('primary')};
    border-radius: 50%;
    color: ${theme.COLOR('bright')};
    display: inline-flex;
    flex: 0 0 auto;
    font-size: 1em;
    font-weight: 400;
    height: 58px;
    width: 58px;
    justify-content: center;
    line-height: 1;
    margin-top: ${theme.UNIT(1)};
  `,
});

const CitationMetaStyles = breakpoints({
  base: css`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: ${theme.UNIT(2)};
    font-size: 1em;
    color: ${theme.COLOR('text-mid')};
  `,
});

const PublisherStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('text')};
  `,
});

const DateStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('text')};
  `,
});

const DoiStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('text')};

    a {
      text-decoration: underline;
      color: ${theme.COLOR('subtle')};
    }
  `,
});

const HeroBlockStyles = breakpoints({
  base: css`
    position: relative;
    width: 100%;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: ${({ $topicColor }: HeroBlockProps) => $topicColor || theme.COLOR('primary')};
      z-index: 1;
    }
  `,
});

const LogoStyles = breakpoints({
  base: css`
    display: block;
    align-self: flex-start;
    height: 62px;
    width: auto;
    margin: 0 0 ${theme.UNIT(3)};
    object-fit: contain;
    object-position: left center;
  `,
});

type HeroBlockProps = {
  $topicColor?: string;
};

type HeroStyleProps = {
  $aspectRatio?: number;
};

const HeroStyles = breakpoints({
  base: css`
    display: block;
    width: 100%;
    margin: 0;
    aspect-ratio: ${({ $aspectRatio }: HeroStyleProps) => $aspectRatio || 16 / 9};
    object-fit: contain;
    object-position: center;
    background: ${theme.COLOR('canvas')};
  `,
});

const CaptionStyles = breakpoints({
  base: css`
    margin: ${theme.UNIT(2)} 0 ${theme.UNIT(4)};
    font-size: ${theme.FONT_SIZE(13)};
    font-weight: 300;
    color: ${theme.COLOR('mid')};
    font-family: ${theme.FONT('sans')};
    line-height: 1.4;
    text-decoration: underline;

    p {
      margin: 0;
    }

    a,
    em,
    i {
      color: inherit;
    }
  `,
});

const PeopleStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: ${theme.UNIT(5)};
    font-family: ${theme.FONT('sans')};
  `,
});

const PeopleGroupStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(1)};
    break-inside: avoid;
    page-break-inside: avoid;
  `,
});

const PeopleLabelStyles = breakpoints({
  base: css`
    color: ${theme.COLOR('dark')};
    font-weight: 300;
    margin-bottom: ${theme.UNIT(2)};
    font-size: 0.9em;
  `,
});

const ContributorListStyles = breakpoints({
  base: css`
    margin: 0;
    font-size: 1em;
    font-weight: 300;
    color: ${theme.COLOR('text-mid')};
    line-height: 1.5;
  `,
});

const ContributorLinkStyles = breakpoints({
  base: css`
    display: inline;
    text-decoration: underline;
    text-underline-offset: 0.04em;
    text-underline-position: from-font;
    text-decoration-thickness: 0.8px;
    color: ${theme.COLOR('mid')};
    font-weight: 300;
  `,
});

const ChairListStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(2)};
  `,
});

const ChairValueStyles = breakpoints({
  base: css`
    break-inside: avoid;
    page-break-inside: avoid;
  `,
});

const LicenseStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(0.5)};
    color: ${theme.COLOR('subtle')};
    font-size: 0.82em;
    font-weight: 300;
    break-inside: avoid;
    page-break-inside: avoid;

    a {
      color: inherit;
      text-decoration: underline;
      text-underline-offset: 0.04em;
      text-underline-position: from-font;
      text-decoration-thickness: 0.8px;
    }
  `,
});

const SectionHeaderStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(1)};
    padding-bottom: ${theme.UNIT(2)};
    border-bottom: 1px solid ${theme.COLOR('line-mid')};
  `,
});

const SectionLabelStyles = breakpoints({
  base: css`
    color: ${({ color }: SectionLabelProps) => color ? theme.COLOR(color) : theme.COLOR('primary')};
    font-size: 0.85em;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  `,
});

const SectionTitleStyles = breakpoints({
  base: css`
    margin: 0;
    color: ${theme.COLOR('strong')};
    font-family: ${theme.FONT('serif')};
    font-size: 2em;
    font-weight: 400;
    line-height: 1.15;
  `,
});

const ContentStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(4)};
    font-weight: 300;

    h2,
    h3 {
      break-after: avoid;
      page-break-after: avoid;
    }

    p,
    li {
      orphans: 3;
      widows: 3;
    }
  `,
});

const IntroStyles = breakpoints({
  base: css`
    font-size: 1.1em;
    font-weight: 400;
    line-height: 1.6;
  `,
});

const BodyContentStyles = breakpoints({
  base: css`
    font-weight: 300;
    line-height: 1.6;
  `,
});

const ChartStyles = breakpoints({
  base: css`
    background: ${theme.COLOR('bright-warm')};
    padding: ${theme.UNIT(4)};
    color: ${theme.COLOR('text')};
    border-radius: 4px;

    .shp--circle-trail-bar-chart-label {
      font-size: 1.1em;
    }
  `,
});

const PspIndicatorGroupsStyles = breakpoints({
  base: css`
    @media print {
      align-self: flex-start;
      width: 100%;
    }
  `,
});

const AnticipatoryImpactLayoutStyles = breakpoints({
  base: css`
    @media print {
      flex-direction: row;
      align-items: flex-start;
      gap: ${theme.UNIT(4)};

      > *:last-child {
        align-self: flex-start;
        width: 40%;
        flex-shrink: 0;
      }
    }
  `,
});

const SubTopicViewStyles = breakpoints({
  base: css`
    padding-top: 0;

    @media print {
      .shp--horizon-grid-layout .shp--grid {
        flex-wrap: nowrap;
      }

      .shp--horizon-grid-layout .shp--grid__item {
        box-sizing: content-box;
        flex: none;
        width: calc(33.333% - 8px) !important;
        max-width: none;
        padding-right: 12px;
        margin-right: 0;
      }

      .shp--horizon-grid-layout .shp--grid__item:nth-child(3n) {
        margin-right: -12px;
      }

      .shp--horizon-indicators__line {
        width: calc(100% - 12px);
      }

      .shp--horizon-indicators__arrow {
        right: 12px;
      }

      .shp--sub-topic-anticipatory-scores-layout {
        flex-direction: row;
        align-items: flex-start;
        gap: ${theme.UNIT(2)};
      }

      .shp--sub-topic-anticipatory-scores-layout > div:first-child {
        width: 50%;
      }

      .shp--sub-topic-anticipatory-scores-layout > *:last-child {
        width: 50%;
        margin: ${theme.UNIT(2)};
      }
    }
  `,
});

const CitationsListStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(2)};
    margin: 0;
    padding-left: 2.5em;
    list-style-position: outside;
    font-size: 0.9em;
    line-height: 1.45;
  `,
});

const CitationStyles = breakpoints({
  base: css`
    break-inside: avoid;
    page-break-inside: avoid;
    overflow: visible;
    word-break: normal;
    overflow-wrap: anywhere;
  `,
});

const CitationGroupStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(2)};
  `,
});

const CitationGroupTitleStyles = breakpoints({
  base: css`
    margin: ${theme.UNIT(3)} 0 ${theme.UNIT(1)};
    color: ${theme.COLOR('primary')};
    font-size: 0.85em;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  `,
});

const FooterStyles = breakpoints({
  base: css`
    margin-top: auto;
    padding-top: ${theme.UNIT(3)};
    border-top: 1px solid ${theme.COLOR('line-mid')};
    color: ${theme.COLOR('subtle')};
    font-size: 0.8em;
    font-weight: 300;
    break-inside: avoid;
    page-break-inside: avoid;
  `,
});

const PrintGlobalStyles = css`
  @page {
    size: A4;
    margin: 18mm 16mm;
  }

  @media print {
    html,
    body {
      background: white !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

// -------- Components -------->

const My = {
  Container: styled.div`${ContainerStyles}${PrintGlobalStyles}`,
    Document: styled.article`${DocumentStyles}`,
      Page: styled.section`${PageStyles}`,
      Cover: styled.section`${PageStyles}${CoverStyles}`,
        Brand: styled.div`${BrandStyles}`,
        CoverMain: styled.div`${CoverMainStyles}`,
          Pretitle: styled(CellLabel)`${PretitleStyles}`,
          TitleRow: styled.div`${TitleRowStyles}`,
            OutlineBadge: styled.div<ColorProps>`${OutlineBadgeStyles}`,
            Title: styled.h1`${TitleStyles}`,
          People: styled.div`${PeopleStyles}`,
            PeopleGroup: styled.div`${PeopleGroupStyles}`,
              PeopleLabel: styled(CellLabel)`${PeopleLabelStyles}`,
              ContributorList: styled.div`${ContributorListStyles}`,
                ContributorLink: styled(EntityLink)<ColorProps>`${ContributorLinkStyles}`,
              ChairList: styled.div`${ChairListStyles}`,
                ChairValue: styled(PersonValue)`${ChairValueStyles}`,
          CitationMeta: styled.div`${CitationMetaStyles}`,
            Publisher: styled.span`${PublisherStyles}`,
            Date: styled.span`${DateStyles}`,
            Doi: styled.span`${DoiStyles}`,
          CoverMedia: styled.div`${CoverMediaStyles}`,
            Logo: styled.img`${LogoStyles}`,
            HeroBlock: styled.div<HeroBlockProps>`${HeroBlockStyles}`,
              Hero: styled.img<HeroStyleProps>`${HeroStyles}`,
            Caption: styled(ContentNode)`${CaptionStyles}`,
          License: styled.div`${LicenseStyles}`,

      SectionHeader: styled.header`${SectionHeaderStyles}`,
        SectionLabel: styled.div<SectionLabelProps>`${SectionLabelStyles}`,
        SectionTitle: styled.h2`${SectionTitleStyles}`,

      Content: styled.div`${ContentStyles}`,
        Intro: styled(ContentEntityIntro)`${IntroStyles}`,
        BodyContent: styled(ContentNode)`${BodyContentStyles}`,
        Chart: styled(TopicAnticipationPotentialChart)`${ChartStyles}`,
        PspIndicatorGroups: styled(TopicPspIndicatorGroups)`${PspIndicatorGroupsStyles}`,
        AnticipatoryImpactLayout: styled(TopicAnticipatoryImpactLayout)`${AnticipatoryImpactLayoutStyles}`,
        SubTopicView: styled(SubTopicViewLayout)`${SubTopicViewStyles}`,
        CitationsList: styled.ol`${CitationsListStyles}`,
          Citation: styled.li`${CitationStyles}`,
        CitationGroup: styled.div`${CitationGroupStyles}`,
          CitationGroupTitle: styled.h3`${CitationGroupTitleStyles}`,

      Footer: styled.footer`${FooterStyles}`,
};

const citationLabel = (citation: Citation) => {
  const authors = (citation.authors || []).map((author) => author.name).filter(Boolean).join(', ');
  const year = citation.year ? ` (${citation.year})` : '';
  const title = citation.name || citation.url || 'Untitled citation';

  return [authors && `${authors}${year}.`, title].filter(Boolean).join(' ');
};

const citationNumber = (citation: Citation, index: number) => {
  const fromFootnote = Number(citation.footnoteNumber);
  return Number.isFinite(fromFootnote) && fromFootnote > 0 ? fromFootnote : index + 1;
};

const collectPrintCitationGroups = (entity: Topic): CitationGroup[] => {
  const topic = entity as Topic & CitationEmbeds;
  const groups: CitationGroup[] = [];
  const topicEmbeds = topic.embeds?.citations || [];

  if (topicEmbeds.length) {
    groups.push({
      id: topic.slug || 'topic',
      title: 'Topic brief',
      citations: topicEmbeds,
    });
  }

  (topic.subTopics || []).forEach((subTopic, index) => {
    const record = subTopic as CitationEmbeds;
    const citations = [
      ...(record.embeds?.citations || []),
      ...(record.horizons || []).flatMap((horizon) => horizon.embeds?.citations || []),
    ];

    if (!citations.length) {
      return;
    }

    groups.push({
      id: record.id || record.slug || `subtopic-${index}`,
      title: [record.outlineNumber, record.name].filter(Boolean).join(' ') || 'SubField',
      citations,
    });
  });

  return groups;
};

export const TopicPrintLayout: Shapeable.FC<TopicPrintLayoutProps> = (props) => {
  const { className, children, entity } = props;
  const t = useLang();
  const monthNames = useMonthNames();
  const indicatorGroups = useIndicatorGroups();

  const currentCommitteeChairs = useCurrentEditionChairs(entity?.committeeChairs || [], entity);
  const currentCommitteeMembers = useCurrentEditionMembers(entity?.committeeMembers || [], entity);

  if (!entity) {
    return null;
  }

  const {
    banner, description, doiId: rawDoiId, intro, name, outlineNumber,
    published, subTopics = [], trend, indicatorValues = [],
    anticipatoryImpact, anticipatoryImpactImage, color,
  } = entity;

  const doiId = normalizeDoiId(rawDoiId);
  const doiUrl = doiUrlFor(doiId);
  const hasDoi = !!doiId;
  const hasChairs = currentCommitteeChairs.length > 0;
  const hasContributors = currentCommitteeMembers.length > 0;
  const formattedDate = published
    ? formatDateString(published.slice(0, 10), t('D MMMM YYYY'), PLATFORM_DATE_FORMAT, { monthNames })
    : '';
  const chairTitle = countLabel(currentCommitteeChairs.length, {
    one: t('Anticipation Committee Chair'),
    n: t('Anticipation Committee Chair'),
  });

  const citationGroups = collectPrintCitationGroups(entity);

  const bannerImage = banner?.image;
  const heroUrl = bannerImage?.url;
  const heroAspect = bannerImage?.width && bannerImage?.height
    && bannerImage.width > 0 && bannerImage.height > 0
    ? bannerImage.width / bannerImage.height
    : 16 / 9;
  const captionLabel = buildTopicBannerPhotoCredit(banner, t('Photo'));

  const hasIndicatorValues = indicatorValues.length > 0;
  const hasSubtopicIndicatorValues = subTopics.some(subTopic => (subTopic.indicatorValues || []).length > 0);

  const hasPspIndicatorGroups = indicatorGroups.some((group) => (
    group.layout?.component === 'IndicatorGroupLayoutPspChart' &&
    (group.indicators || []).some((indicator) => (
      indicatorValues.some((indicatorValue) => indicatorValue.indicator?.id === indicator.id)
    ))
  ));

  const hasAnticipatoryImpact =
    anticipatoryImpact && anticipatoryImpact.text && (
      hasPspIndicatorGroups ||
      (anticipatoryImpactImage && anticipatoryImpactImage.image && anticipatoryImpactImage.image.url)
    );

  return (
    <EntityProvider value={entity}>
    <My.Container className={cls.name(className)}>
      <My.Document>
        <My.Cover>
          <My.CoverMedia>
            <My.Logo src={GESDA_LOGO} alt="GESDA" />
            {heroUrl && (
              <My.HeroBlock $topicColor={color?.value}>
                <My.Hero
                  src={heroUrl}
                  alt={banner?.name || name}
                  $aspectRatio={heroAspect}
                />
              </My.HeroBlock>
            )}
            {captionLabel && <My.Caption entity={captionLabel} />}
          </My.CoverMedia>

          <My.CoverMain>
            <My.TitleRow>
              <My.Pretitle>{'Topic'}</My.Pretitle>
              <My.Title>{name}</My.Title>
              {/* {outlineNumber && <My.OutlineBadge color={entity.color?.value}>{outlineNumber}</My.OutlineBadge>} */}
            </My.TitleRow>
            {
              (hasChairs || hasContributors) && (
                <My.People>
                  {
                    hasChairs &&
                    <My.PeopleGroup>
                      <My.PeopleLabel>{chairTitle}:</My.PeopleLabel>
                      <My.ChairList>
                        {currentCommitteeChairs.map((chair) => (
                          <My.ChairValue key={chair.id || chair.slug} entity={chair} />
                        ))}
                      </My.ChairList>
                    </My.PeopleGroup>
                  }
                  {
                    hasContributors &&
                    <My.PeopleGroup>
                      <My.PeopleLabel>{t('Anticipation Committee')}:</My.PeopleLabel>
                      <My.ContributorList>
                        {
                          currentCommitteeMembers.map((member, index) => {
                            const isLast = index === currentCommitteeMembers.length - 1;

                            return (
                              <React.Fragment key={member.id || member.slug || index}>
                                <My.ContributorLink entity={member} color={color?.value}>
                                  {member.name}
                                </My.ContributorLink>
                                {!isLast && ', '}
                              </React.Fragment>
                            );
                          })
                        }
                      </My.ContributorList>
                    </My.PeopleGroup>
                  }
                </My.People>
              )
            }
            {
              hasDoi && (
                <My.CitationMeta>
                  <My.Publisher>{t('GESDA')}</My.Publisher>
                  {!!formattedDate && <My.Date>{formattedDate}</My.Date>}
                  <My.Doi>
                    {t('DOI:')}{' '}
                    <a href={doiUrl}>{doiId}</a>
                  </My.Doi>
                </My.CitationMeta>
              )
            }
          </My.CoverMain>

          <My.Footer>
            <My.License>
              Under a Creative Commons <a href={CREATIVE_COMMONS_BY_SA_URL}>CC BY-SA license</a>
            </My.License>
          </My.Footer>
        </My.Cover>

        <My.Page>
          <My.SectionHeader>
            <My.SectionLabel color={color?.value}>Topic Brief</My.SectionLabel>
            <My.SectionTitle>{name}</My.SectionTitle>
          </My.SectionHeader>
          <My.Content>
            <My.Intro entity={entity} />
            <My.BodyContent entity={intro || description} />
            {intro && description && <My.BodyContent entity={description} />}
            {
              (hasIndicatorValues || hasSubtopicIndicatorValues) && (
                <LightMode>
                  <My.Chart entity={entity} animate={false} />
                </LightMode>
              )
            }
            {
              hasPspIndicatorGroups ? (
                <My.AnticipatoryImpactLayout entity={entity}>
                  <My.PspIndicatorGroups animate={false} entity={entity} />
                </My.AnticipatoryImpactLayout>
              ) : hasAnticipatoryImpact && (
                <My.AnticipatoryImpactLayout entity={entity} />
              )
            }
          </My.Content>
        </My.Page>

        {subTopics.map((subTopic) => (
          <My.Page key={subTopic.id || subTopic.slug}>
            <My.SectionHeader>
              <My.SectionLabel color={subTopic.color?.value}>SubField</My.SectionLabel>
              <My.SectionTitle>{subTopic.name}</My.SectionTitle>
            </My.SectionHeader>
            <My.Content>
              <My.SubTopicView entity={subTopic} animateHorizons={false} />
            </My.Content>
          </My.Page>
        ))}

        <My.Page>
          <My.SectionHeader>
            <My.SectionLabel color={color?.value}>References</My.SectionLabel>
            <My.SectionTitle>Citations</My.SectionTitle>
          </My.SectionHeader>
          <My.Content>
            {
              citationGroups.length > 0 ? (
                citationGroups.map((group) => (
                  <My.CitationGroup key={group.id}>
                    <My.CitationGroupTitle>{group.title}</My.CitationGroupTitle>
                    <My.CitationsList>
                      {group.citations.map((citation, index) => (
                        <My.Citation
                          key={`${group.id}-${index}-${citation.slug || citation.url || citation.name || index}`}
                          value={citationNumber(citation, index)}
                        >
                          {citationLabel(citation)}
                          {citation.url && <> <a href={citation.url}>{citation.url}</a></>}
                        </My.Citation>
                      ))}
                    </My.CitationsList>
                  </My.CitationGroup>
                ))
              ) : (
                <p>No citations available for this Topic.</p>
              )
            }
          </My.Content>
        </My.Page>
      </My.Document>
      {children}
    </My.Container>
    </EntityProvider>
  );
};

TopicPrintLayout.defaultProps = TopicPrintLayoutDefaultProps;
TopicPrintLayout.cls = cls;